import React, { useState, useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut } from "lucide-react";
import { fotoDownloadUrl } from "@/lib/fotoUtils";

/**
 * Lightbox modal con navegación por teclado, ZOOM (rueda, botones, doble click,
 * arrastre para mover) y descarga con nombre personalizado.
 *
 * Props:
 *  - fotos: [{ url, categoria, latitud, longitud, ...}]
 *  - openIdx: índice inicial (null para cerrado)
 *  - onClose: () => void
 *  - title: string opcional
 *  - nameForIdx: (i) => string — nombre base (sin extensión) para descargar la foto i
 */
export default function PhotoLightbox({ fotos = [], openIdx = null, onClose, title, nameForIdx }) {
  const [idx, setIdx] = useState(openIdx);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const imgWrapRef = useRef(null);

  useEffect(() => { setIdx(openIdx); }, [openIdx]);
  useEffect(() => { setScale(1); setOffset({ x: 0, y: 0 }); }, [idx]);

  const next = useCallback(() => {
    if (idx == null) return;
    setIdx((i) => (i + 1) % fotos.length);
  }, [idx, fotos.length]);

  const prev = useCallback(() => {
    if (idx == null) return;
    setIdx((i) => (i - 1 + fotos.length) % fotos.length);
  }, [idx, fotos.length]);

  const close = useCallback(() => {
    setIdx(null);
    onClose && onClose();
  }, [onClose]);

  useEffect(() => {
    if (idx == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "+" || e.key === "=") setScale((s) => Math.min(5, s * 1.4));
      else if (e.key === "-") setScale((s) => { const n = Math.max(1, s / 1.4); if (n === 1) setOffset({ x: 0, y: 0 }); return n; });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, next, prev, close]);

  // Rueda del ratón para zoom (listener nativo no-pasivo para poder preventDefault)
  useEffect(() => {
    const el = imgWrapRef.current;
    if (!el || idx == null) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      setScale((s) => {
        const n = Math.min(5, Math.max(1, s * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
        if (n === 1) setOffset({ x: 0, y: 0 });
        return n;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [idx]);

  if (idx == null || !fotos[idx]) return null;
  const f = fotos[idx];
  const isVideo = f.tipo === "video" || /\.(mp4|mov|webm|m4v)(\?|$)/i.test(f.url || "");
  const downloadName = nameForIdx ? nameForIdx(idx) : null;
  const downloadHref = downloadName ? fotoDownloadUrl(f.url, downloadName) : f.url;

  const onPointerDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setOffset({
      x: dragRef.current.ox + (e.clientX - dragRef.current.x),
      y: dragRef.current.oy + (e.clientY - dragRef.current.y),
    });
  };
  const onPointerUp = () => { dragRef.current = null; };
  const toggleZoom = () => {
    setScale((s) => (s > 1 ? 1 : 2.5));
    setOffset({ x: 0, y: 0 });
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/95 backdrop-blur flex items-center justify-center"
      data-testid="photo-lightbox"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      {/* Header */}
      <div className="absolute top-0 inset-x-0 px-4 py-3 flex items-center justify-between text-zinc-200 z-10">
        <div className="text-sm truncate max-w-[55%]">
          {title && <span className="text-zinc-400">{title} · </span>}
          <span className="font-semibold">{idx + 1} / {fotos.length}</span>
          {f.categoria && <span className="ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">{f.categoria}</span>}
        </div>
        <div className="flex items-center gap-2">
          {!isVideo && (
            <>
              <button
                onClick={() => setScale((s) => { const n = Math.max(1, s / 1.4); if (n === 1) setOffset({ x: 0, y: 0 }); return n; })}
                data-testid="lightbox-zoom-out"
                className="bg-zinc-900/70 hover:bg-zinc-800 text-zinc-200 rounded-lg p-1.5 border border-zinc-700 disabled:opacity-40"
                disabled={scale <= 1}
                title="Alejar (-)"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-[10px] text-zinc-400 w-9 text-center select-none">{Math.round(scale * 100)}%</span>
              <button
                onClick={() => setScale((s) => Math.min(5, s * 1.4))}
                data-testid="lightbox-zoom-in"
                className="bg-zinc-900/70 hover:bg-zinc-800 text-zinc-200 rounded-lg p-1.5 border border-zinc-700 disabled:opacity-40"
                disabled={scale >= 5}
                title="Acercar (+)"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </>
          )}
          <a
            href={downloadHref}
            target="_blank"
            rel="noreferrer"
            download={downloadName ? `${downloadName}.jpg` : true}
            data-testid="lightbox-download"
            className="bg-zinc-900/70 hover:bg-zinc-800 text-zinc-200 rounded-lg px-2.5 py-1.5 text-xs inline-flex items-center gap-1.5 border border-zinc-700"
            title={downloadName ? `Descargar ${downloadName}.jpg` : "Descargar"}
          >
            <Download className="w-3.5 h-3.5" />
            {downloadName && <span className="hidden sm:inline max-w-[160px] truncate">{downloadName}.jpg</span>}
          </a>
          <button
            onClick={close}
            data-testid="lightbox-close"
            className="bg-zinc-900/70 hover:bg-zinc-800 text-zinc-200 rounded-lg p-1.5 border border-zinc-700"
            title="Cerrar (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Prev */}
      {fotos.length > 1 && (
        <button
          onClick={prev}
          data-testid="lightbox-prev"
          className="absolute left-3 top-1/2 -translate-y-1/2 bg-zinc-900/70 hover:bg-zinc-800 text-zinc-200 rounded-full p-2 border border-zinc-700 z-10"
          aria-label="Anterior"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}

      {/* Image or video */}
      {isVideo ? (
        <video
          src={f.url}
          poster={f.thumbnail_url || undefined}
          controls
          autoPlay
          playsInline
          className="max-w-[92vw] max-h-[88vh] select-none rounded-md shadow-2xl bg-black"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <div
          ref={imgWrapRef}
          className="overflow-hidden max-w-[92vw] max-h-[85vh] flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          style={{ cursor: scale > 1 ? "grab" : "zoom-in", touchAction: "none" }}
        >
          <img
            src={f.url}
            alt={f.categoria || "foto"}
            onDoubleClick={toggleZoom}
            className="max-w-[92vw] max-h-[85vh] object-contain select-none rounded-md shadow-2xl"
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transition: dragRef.current ? "none" : "transform 0.15s ease-out" }}
            draggable={false}
          />
        </div>
      )}

      {/* Next */}
      {fotos.length > 1 && (
        <button
          onClick={next}
          data-testid="lightbox-next"
          className="absolute right-3 top-1/2 -translate-y-1/2 bg-zinc-900/70 hover:bg-zinc-800 text-zinc-200 rounded-full p-2 border border-zinc-700 z-10"
          aria-label="Siguiente"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      {/* Footer thumbnails */}
      {fotos.length > 1 && (
        <div className="absolute bottom-0 inset-x-0 px-4 py-3 flex gap-1.5 overflow-x-auto justify-center">
          {fotos.map((ff, i) => {
            const ffVideo = ff.tipo === "video" || /\.(mp4|mov|webm|m4v)(\?|$)/i.test(ff.url || "");
            const thumb = ffVideo
              ? (ff.thumbnail_url || (ff.url || "").replace(/\.(mp4|mov|webm|m4v)(\?.*)?$/i, ".jpg"))
              : ff.url;
            return (
              <button
                key={ff.id || i}
                onClick={() => setIdx(i)}
                className={`shrink-0 w-12 h-12 rounded border-2 overflow-hidden transition-all relative ${
                  i === idx ? "border-amber-400 scale-110" : "border-zinc-700 opacity-60 hover:opacity-100"
                }`}
              >
                <img src={thumb} alt="" className="w-full h-full object-cover" />
                {ffVideo && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-black/60 rounded-full w-5 h-5 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-3 h-3 fill-white"><path d="M8 5v14l11-7z" /></svg>
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
